import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import type { FleetJobRow } from "@/components/fleet-capacity/types";
import { computeJobTimes, nzDateStrFromIso } from "@/components/fleet-capacity/utils";
import { getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { driverDisplayName } from "./utils";
import type { ShiftRow } from "./types";

export interface AllocationMessageOptions {
  includeSedanSuv: boolean;
  includeTmv: boolean;
  includeSts: boolean;
  includeBreaks: boolean;
}

export interface AllocationBreak {
  vehicle_number: string;
  startHHMM: string;
  endHHMM: string;
}

export type AllocationJob = FleetJobRow & { company_name: string | null };

// Same shape as fleet-capacity/utils.ts's own FLEET_JOB_SELECT, plus
// company_name -- duplicated rather than imported since that constant isn't
// exported (matches this codebase's established per-feature duplication
// convention for TaxiCaller-derived selects).
const JOB_SELECT =
  'id, "Job_ID", vehicle_number, driver_name, pu_date, pu_time, do_date, do_time, "Pick_Up_Location", "Drop_Off_Location", "Stage", job_km, "Passenger_First_Name", "Passenger_Last_Name", estimated_duration_seconds, requested_vehicle_type, requires_wheelchair_vehicle, requested_pickup_at, passenger_count, wheelchair_count, luggage_count, assigned_at, company_name';

function addUtcDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}

const CLOSED_STAGES = new Set(["Cancelled", "No show"]);

/**
 * Every one of a single NZ calendar day's bookings, regardless of vehicle or
 * company -- classified client-side afterwards (see computeAllocationCandidates)
 * rather than fetched per-type like Fleet Capacity does, since it needs one
 * flat pool to split across Sedan/SUV, TMV and STS sections. Same
 * three-bucket pu_date/assigned_at/requested_pickup_at backfill chain as the
 * Fleet Capacity fetches, just scoped to one exact date instead of a range.
 */
export async function fetchJobsForDate(dateStr: string): Promise<AllocationJob[]> {
  const [byPuDate, byAssigned, byRequested] = await Promise.all([
    supabase.from("taxicaller_data").select(JOB_SELECT).eq("pu_date", dateStr),
    supabase
      .from("taxicaller_data")
      .select(JOB_SELECT)
      .is("pu_date", null)
      .not("assigned_at", "is", null)
      .gte("assigned_at", addUtcDays(dateStr, -1))
      .lte("assigned_at", addUtcDays(dateStr, 1)),
    supabase
      .from("taxicaller_data")
      .select(JOB_SELECT)
      .is("pu_date", null)
      .is("assigned_at", null)
      .not("requested_pickup_at", "is", null)
      .gte("requested_pickup_at", addUtcDays(dateStr, -1))
      .lte("requested_pickup_at", addUtcDays(dateStr, 1)),
  ]);
  if (byPuDate.error) throw byPuDate.error;
  if (byAssigned.error) throw byAssigned.error;
  if (byRequested.error) throw byRequested.error;

  const all = [
    ...((byPuDate.data as AllocationJob[]) || []),
    ...((byAssigned.data as AllocationJob[]) || []),
    ...((byRequested.data as AllocationJob[]) || []),
  ];
  const byId = new Map<string, AllocationJob>();
  for (const job of all) {
    if (CLOSED_STAGES.has(job.Stage || "")) continue;
    const matchesDate =
      job.pu_date === dateStr ||
      nzDateStrFromIso(job.assigned_at) === dateStr ||
      nzDateStrFromIso(job.requested_pickup_at) === dateStr;
    if (matchesDate) byId.set(job.id, job);
  }
  return Array.from(byId.values());
}

/** Published breaks for one day, keyed by vehicle_number -- same table/shape BreaksTab.tsx already reads. */
export async function fetchPublishedBreaksForDate(dateStr: string): Promise<AllocationBreak[]> {
  const { data, error } = await supabase
    .from("roster_scheduled_breaks")
    .select("vehicle_number, suggested_start_hhmm, suggested_end_hhmm, confirmed_start_hhmm, confirmed_end_hhmm, status")
    .eq("break_date", dateStr)
    .eq("status", "published");
  if (error) throw error;
  return (data || [])
    .filter((row) => row.vehicle_number)
    .map((row) => ({
      vehicle_number: row.vehicle_number as string,
      startHHMM: (row.confirmed_start_hhmm || row.suggested_start_hhmm || "").slice(0, 5),
      endHHMM: (row.confirmed_end_hhmm || row.suggested_end_hhmm || "").slice(0, 5),
    }))
    .filter((b) => b.startHHMM && b.endHHMM);
}

function ordinalSuffix(day: number): string {
  if (day >= 11 && day <= 13) return "th";
  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
}

/** "2026-08-02" -> "Sunday 2nd of August 2026" */
export function formatDayLabel(dateStr: string): string {
  const date = new Date(`${dateStr}T00:00:00`);
  const day = date.getDate();
  return `${format(date, "EEEE")} ${day}${ordinalSuffix(day)} of ${format(date, "MMMM yyyy")}`;
}

/**
 * "23 Achilles Court, Highbury, Palmerston North, New Zealand" -> "Achilles
 * Court". A best-effort trim (first comma-segment, house/unit number
 * stripped) -- not a real short-name lookup, so it won't produce hand-picked
 * nicknames like "CD Plaza"; close enough to skim without full addresses.
 */
export function shortPickupLabel(fullAddress: string | null | undefined): string {
  if (!fullAddress) return "?";
  const firstSegment = fullAddress.split(",")[0]?.trim() || fullAddress.trim();
  const withoutNumber = firstSegment.replace(/^\d+[a-zA-Z]?(?:\/\d+[a-zA-Z]?)?\s+/, "").trim();
  return withoutNumber || firstSegment || "?";
}

function jobStartH(job: AllocationJob): number {
  return computeJobTimes(job).startH;
}

function hhmmFromDecimalHours(h: number): string {
  const hh = Math.floor(h) % 24;
  const mm = Math.round((h - Math.floor(h)) * 60);
  return `${String(hh).padStart(2, "0")}${String(mm).padStart(2, "0")}`;
}

/** "0430 Caroline @ Cook St" -- first name only, per Janelle's preference. */
export function jobLine(job: AllocationJob): string {
  const name = job.Passenger_First_Name || "Unknown";
  return `${hhmmFromDecimalHours(jobStartH(job))} ${name} @ ${shortPickupLabel(job.Pick_Up_Location)}`;
}

function firstName(shift: ShiftRow): string {
  return (shift.driver_firstname || shift.driver_name.split(" ")[0] || shift.driver_name).trim();
}

function breakSuffix(vehicleNumber: string | null | undefined, breaks: AllocationBreak[]): string {
  if (!vehicleNumber) return "";
  const brk = breaks.find((b) => b.vehicle_number === vehicleNumber);
  return brk ? ` - Break: ${brk.startHHMM} - ${brk.endHHMM}` : "";
}

function rosterLines(shifts: ShiftRow[], breaks: AllocationBreak[], includeBreaks: boolean): string {
  return [...shifts]
    .sort((a, b) => a.start_hhmm.localeCompare(b.start_hhmm) || driverDisplayName(a).localeCompare(driverDisplayName(b)))
    .map((s) => {
      const veh = s.vehicle_number || "???";
      const suffix = includeBreaks ? breakSuffix(s.vehicle_number, breaks) : "";
      return `${veh} - ${driverDisplayName(s)} (${s.start_hhmm}-${s.end_hhmm})${suffix}`;
    })
    .join("\n");
}

type VehicleCategory = "sedan_suv" | "tmv" | "sts";

/**
 * STS shifts are identified by shift_type_code prefix (STS_CAR/STS_LATE/
 * STS_MID -- same convention as the Fleet Capacity STS tab), checked before
 * vehicle type since an STS driver may well be driving a real Sedan.
 * Everything else is classified by the vehicle's own icon_style, same
 * car/truck split Fleet Capacity's tab grouping already uses.
 */
function classifyShift(shift: ShiftRow, vehicleTypes: VehicleTypeRow[]): VehicleCategory | null {
  if (!shift.vehicle_number) return null;
  if ((shift.shift_type_code || "").toUpperCase().startsWith("STS_")) return "sts";
  return getIconStyle(shift.vehicle_type, vehicleTypes) === "truck" ? "tmv" : "sedan_suv";
}

export interface VehicleCategoryGroup {
  label: string;
  shifts: ShiftRow[];
}

/**
 * Regroups a day's shifts by what they're ACTUALLY driving that day (same
 * classifyShift used for the job sections) rather than by the driver's
 * nominal schedule_group -- a driver whose home team is Car but who's
 * rostered onto a Wheelchair vehicle that day should show under TMV Team in
 * the message, not Car Team. Deliberately separate from computedAllocationGroups
 * in RosterPage.tsx, which stays schedule_group-based for the plain "Copy"
 * cards. Shifts with no vehicle assigned yet can't be classified this way
 * and land in a final "Unassigned" bucket instead of silently vanishing.
 */
export function groupShiftsByVehicleCategory(shifts: ShiftRow[], vehicleTypes: VehicleTypeRow[]): VehicleCategoryGroup[] {
  const byCategory: Record<VehicleCategory | "unassigned", ShiftRow[]> = {
    sedan_suv: [],
    tmv: [],
    sts: [],
    unassigned: [],
  };
  for (const s of shifts) {
    byCategory[classifyShift(s, vehicleTypes) ?? "unassigned"].push(s);
  }
  const labels: [VehicleCategory | "unassigned", string][] = [
    ["sedan_suv", "Car Team"],
    ["sts", "STS Team"],
    ["tmv", "TMV Team"],
    ["unassigned", "Unassigned"],
  ];
  return labels.filter(([key]) => byCategory[key].length > 0).map(([key, label]) => ({ label, shifts: byCategory[key] }));
}

/** One candidate job for the review checklist -- `defaultIncluded` seeds the checkbox's initial state, nothing more. */
export interface CandidateJob {
  job: AllocationJob;
  defaultIncluded: boolean;
}

export interface DriverJobGroup {
  shift: ShiftRow;
  jobs: CandidateJob[];
}

export interface TeamCandidates {
  drivers: DriverJobGroup[];
  otherJobs: CandidateJob[];
}

export interface AllocationCandidates {
  sedanSuv: CandidateJob[];
  tmv: TeamCandidates;
  sts: TeamCandidates;
}

const EARLY_CUTOFF_H = 12; // noon

/**
 * Works out every job that COULD go in the message, without deciding which
 * ones actually will -- that's left to the dispatcher via the review
 * checklist (see defaultSelectedJobIds for the starting state, and
 * buildAllocationMessage for how a selection turns into final text).
 *
 * Sedan/SUV: every booking due before EARLY_CUTOFF_H (regardless of vehicle
 * or whether one's assigned yet), excluding anything already flagged
 * wheelchair/TMV or STS-company-tagged. Deliberately NOT tied to "whoever's
 * on shift first" -- a driver-window model (tried and discarded) kept
 * missing real early jobs whenever the day had an odd short opener shift or
 * several Car drivers starting close together with no clean single
 * "solo" one. Every job here defaults to UNCHECKED, same reasoning as
 * before: this is the most heuristic section, so nothing goes out without a
 * deliberate pick.
 *
 * TMV/STS: every driver's full day of jobs is a candidate, all defaulting
 * to CHECKED (dispatcher unchecks anything that shouldn't go out), plus an
 * "other jobs" list of that category's still-unassigned bookings.
 */
export function computeAllocationCandidates(
  allShiftsToday: ShiftRow[],
  jobs: AllocationJob[],
  vehicleTypes: VehicleTypeRow[]
): AllocationCandidates {
  const jobsByVehicle = new Map<string, AllocationJob[]>();
  for (const job of jobs) {
    if (!job.vehicle_number) continue;
    if (!jobsByVehicle.has(job.vehicle_number)) jobsByVehicle.set(job.vehicle_number, []);
    jobsByVehicle.get(job.vehicle_number)!.push(job);
  }

  const vehicleCategoryByNumber = new Map<string, VehicleCategory>();
  for (const s of allShiftsToday) {
    if (!s.vehicle_number) continue;
    const cat = classifyShift(s, vehicleTypes);
    if (cat) vehicleCategoryByNumber.set(s.vehicle_number, cat);
  }

  const sedanSuv: CandidateJob[] = jobs
    .filter((j) => {
      if (jobStartH(j) >= EARLY_CUTOFF_H - 1e-9) return false;
      if (j.requires_wheelchair_vehicle || j.requested_vehicle_type === "Wheelchair Van") return false;
      if ((j.company_name || "").toLowerCase().includes("specialty")) return false;
      if (j.vehicle_number) {
        const cat = vehicleCategoryByNumber.get(j.vehicle_number);
        if (cat && cat !== "sedan_suv") return false;
      }
      return true;
    })
    .sort((a, b) => jobStartH(a) - jobStartH(b))
    .map((job) => ({ job, defaultIncluded: false }));

  const buildTeam = (category: "tmv" | "sts"): TeamCandidates => {
    const teamShifts = allShiftsToday
      .filter((s) => classifyShift(s, vehicleTypes) === category)
      .sort((a, b) => a.start_hhmm.localeCompare(b.start_hhmm));

    const drivers: DriverJobGroup[] = teamShifts
      .map((shift) => ({
        shift,
        jobs: (jobsByVehicle.get(shift.vehicle_number || "") || [])
          .sort((a, b) => jobStartH(a) - jobStartH(b))
          .map((job) => ({ job, defaultIncluded: true })),
      }))
      .filter((d) => d.jobs.length > 0);

    const otherJobs: CandidateJob[] = jobs
      .filter((j) => {
        if (j.vehicle_number) return false; // already covered above, or belongs to a different category's vehicle
        return category === "tmv"
          ? j.requires_wheelchair_vehicle || j.requested_vehicle_type === "Wheelchair Van"
          : (j.company_name || "").toLowerCase().includes("specialty");
      })
      .sort((a, b) => jobStartH(a) - jobStartH(b))
      .map((job) => ({ job, defaultIncluded: true }));

    return { drivers, otherJobs };
  };

  return { sedanSuv, tmv: buildTeam("tmv"), sts: buildTeam("sts") };
}

/** The starting checkbox state for a fresh set of candidates -- see computeAllocationCandidates for what defaults to which. */
export function defaultSelectedJobIds(candidates: AllocationCandidates): Set<string> {
  const ids = new Set<string>();
  const add = (cj: CandidateJob) => {
    if (cj.defaultIncluded) ids.add(cj.job.id);
  };
  candidates.sedanSuv.forEach(add);
  candidates.tmv.drivers.forEach((d) => d.jobs.forEach(add));
  candidates.tmv.otherJobs.forEach(add);
  candidates.sts.drivers.forEach((d) => d.jobs.forEach(add));
  candidates.sts.otherJobs.forEach(add);
  return ids;
}

function linesForSelected(jobs: CandidateJob[], selectedJobIds: Set<string>): string[] {
  return jobs.filter((cj) => selectedJobIds.has(cj.job.id)).map((cj) => jobLine(cj.job));
}

function appendTeamBlock(lines: string[], team: TeamCandidates, selectedJobIds: Set<string>, heading: string): void {
  if (team.drivers.length === 0 && team.otherJobs.length === 0) return;

  const driverLines = team.drivers
    .map((d) => ({ shift: d.shift, selected: linesForSelected(d.jobs, selectedJobIds) }))
    .filter((d) => d.selected.length > 0);
  const otherSelected = linesForSelected(team.otherJobs, selectedJobIds);

  if (driverLines.length === 0 && otherSelected.length === 0) return;

  lines.push("", heading);
  for (const d of driverLines) {
    lines.push("", `${firstName(d.shift).toUpperCase()} TO DO`);
    lines.push(d.selected.join("\n"));
  }
  if (otherSelected.length > 0) {
    lines.push("", "OTHER JOBS");
    lines.push(otherSelected.join("\n"));
  }
}

export interface BuildAllocationMessageParams {
  dayLabel: string;
  /** Real vehicle-type groups for the target day -- see groupShiftsByVehicleCategory. */
  vehicleCategoryGroups: VehicleCategoryGroup[];
  breaks: AllocationBreak[];
  candidates: AllocationCandidates;
  selectedJobIds: Set<string>;
  options: AllocationMessageOptions;
}

export function buildAllocationMessage(params: BuildAllocationMessageParams): string {
  const { dayLabel, vehicleCategoryGroups, breaks, candidates, selectedJobIds, options } = params;

  const lines: string[] = ["Good evening!", "Here's the list of jobs for tomorrow", dayLabel];

  for (const group of vehicleCategoryGroups) {
    lines.push("", `--- ${group.label} ---`, "");
    lines.push(rosterLines(group.shifts, breaks, options.includeBreaks));
  }

  if (options.includeSedanSuv) {
    const selected = linesForSelected(candidates.sedanSuv, selectedJobIds);
    if (selected.length > 0) {
      lines.push("", "EARLY MORNING JOBS", "");
      lines.push(selected.join("\n"));
    }
  }

  if (options.includeTmv) appendTeamBlock(lines, candidates.tmv, selectedJobIds, "TMV's:");
  if (options.includeSts) appendTeamBlock(lines, candidates.sts, selectedJobIds, "STS's:");

  lines.push("", "Please confirm", "Thank you! :)");

  return lines.join("\n");
}
